import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Loader2, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/api";
import { toast } from "sonner";

const emptyForm = {
  title: "", subtitle: "", description: "", price: "", cover_url: "",
  pages: 180, year: "", category: "Legends", featured: false,
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("mlm_token"));
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [ebooks, setEbooks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/ebooks").then(({ data }) => setEbooks(data)).catch(() => {});

  useEffect(() => {
    if (token) load();
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", loginForm);
      localStorage.setItem("mlm_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setLoginError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("mlm_token");
    setToken(null);
  };

  const openForm = (book) => {
    setEditing(book || "new");
    setForm(book ? { ...book } : emptyForm);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0, pages: parseInt(form.pages) || 0 };
    try {
      if (editing === "new") {
        await api.post("/ebooks", payload);
        toast.success("Ebook added to the catalog.");
      } else {
        await api.put(`/ebooks/${editing.id}`, payload);
        toast.success("Ebook updated.");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (book) => {
    try {
      await api.delete(`/ebooks/${book.id}`);
      toast.success(`"${book.title}" removed.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!token) {
    return (
      <main className="min-h-screen bg-background text-foreground flex items-center justify-center px-6" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-foreground/40 hover:text-primary transition-colors duration-300 mb-10" data-testid="admin-back-home-link">
            <ArrowLeft size={14} /> Back to site
          </Link>
          <p className="text-xs font-semibold tracking-[0.4em] uppercase text-primary mb-3">Restricted Paddock</p>
          <h1 className="font-display uppercase text-4xl mb-10" data-testid="admin-login-heading">Admin Access</h1>
          <form onSubmit={login} className="space-y-6" data-testid="admin-login-form">
            <input
              type="email" required placeholder="Email"
              value={loginForm.email}
              onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 focus:border-primary py-4 text-sm outline-none transition-colors duration-300 placeholder:text-foreground/30"
              data-testid="admin-login-email-input"
            />
            <input
              type="password" required placeholder="Password"
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 focus:border-primary py-4 text-sm outline-none transition-colors duration-300 placeholder:text-foreground/30"
              data-testid="admin-login-password-input"
            />
            {loginError && <p className="text-primary text-xs" data-testid="admin-login-error">{loginError}</p>}
            <button
              type="submit" disabled={loggingIn}
              className="w-full bg-primary text-white font-display uppercase tracking-wider text-sm py-4 hover:bg-[#a00d26] active:scale-95 disabled:opacity-50 transition-[background-color,transform,opacity] duration-200 flex items-center justify-center gap-3"
              data-testid="admin-login-submit"
            >
              {loggingIn && <Loader2 size={14} className="animate-spin" />} Enter
            </button>
          </form>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background text-foreground" data-testid="admin-dashboard">
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/60 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <span className="font-display text-sm tracking-wide">MOTOR<span className="text-primary">LEGENDS</span>MEDIA — ADMIN</span>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-[11px] uppercase tracking-[0.25em] text-foreground/40 hover:text-primary transition-colors duration-300" data-testid="admin-view-site-link">View site</Link>
            <button onClick={logout} className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-foreground/40 hover:text-primary transition-colors duration-300" data-testid="admin-logout-button">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <div className="flex items-center justify-between mb-10">
          <h1 className="font-display uppercase text-3xl" data-testid="admin-catalog-heading">Ebook Catalog</h1>
          <button
            onClick={() => openForm(null)}
            className="flex items-center gap-2 bg-primary text-white font-display uppercase tracking-wider text-xs px-6 py-3 hover:bg-[#a00d26] active:scale-95 transition-[background-color,transform] duration-200"
            data-testid="admin-add-ebook-button"
          >
            <Plus size={14} /> Add Ebook
          </button>
        </div>

        {editing && (
          <motion.form
            onSubmit={save}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="border border-white/20 p-8 mb-12 grid md:grid-cols-2 gap-6"
            data-testid="admin-ebook-form"
          >
            <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-black/40 border border-white/20 px-4 py-3 text-sm outline-none focus:border-primary transition-colors" data-testid="admin-form-title" />
            <input placeholder="Subtitle" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="bg-black/40 border border-white/20 px-4 py-3 text-sm outline-none focus:border-primary transition-colors" data-testid="admin-form-subtitle" />
            <textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-2 bg-black/40 border border-white/20 px-4 py-3 text-sm outline-none focus:border-primary transition-colors resize-none" data-testid="admin-form-description" />
            <input required type="number" step="0.01" placeholder="Price (USD)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-black/40 border border-white/20 px-4 py-3 text-sm outline-none focus:border-primary transition-colors" data-testid="admin-form-price" />
            <input required placeholder="Cover image URL" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} className="bg-black/40 border border-white/20 px-4 py-3 text-sm outline-none focus:border-primary transition-colors" data-testid="admin-form-cover" />
            <input type="number" placeholder="Pages" value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} className="bg-black/40 border border-white/20 px-4 py-3 text-sm outline-none focus:border-primary transition-colors" data-testid="admin-form-pages" />
            <input placeholder="Era (e.g. 1950–1970)" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="bg-black/40 border border-white/20 px-4 py-3 text-sm outline-none focus:border-primary transition-colors" data-testid="admin-form-year" />
            <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-black/40 border border-white/20 px-4 py-3 text-sm outline-none focus:border-primary transition-colors" data-testid="admin-form-category" />
            <label className="flex items-center gap-3 text-xs uppercase tracking-widest text-foreground/60" data-testid="admin-form-featured-label">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#c8102e] w-4 h-4" data-testid="admin-form-featured" />
              Featured
            </label>
            <div className="md:col-span-2 flex gap-4">
              <button type="submit" disabled={saving} className="bg-primary text-white font-display uppercase tracking-wider text-xs px-8 py-3 hover:bg-[#a00d26] active:scale-95 disabled:opacity-50 transition-[background-color,transform,opacity] duration-200 flex items-center gap-2" data-testid="admin-form-save-button">
                {saving && <Loader2 size={13} className="animate-spin" />} Save
              </button>
              <button type="button" onClick={() => setEditing(null)} className="border border-white/20 text-foreground/60 font-display uppercase tracking-wider text-xs px-8 py-3 hover:border-primary hover:text-primary transition-colors duration-200" data-testid="admin-form-cancel-button">
                Cancel
              </button>
            </div>
          </motion.form>
        )}

        <div className="border border-white/10" data-testid="admin-ebook-table">
          <div className="grid grid-cols-[3rem_1fr_6rem_8rem_6rem] gap-4 px-6 py-4 border-b border-white/10 text-[10px] uppercase tracking-[0.25em] text-foreground/40">
            <span></span><span>Title</span><span>Price</span><span>Era</span><span className="text-right">Actions</span>
          </div>
          {ebooks.map((book) => (
            <div key={book.id} className="grid grid-cols-[3rem_1fr_6rem_8rem_6rem] gap-4 px-6 py-4 border-b border-white/5 items-center hover:bg-white/[0.03] transition-colors duration-200" data-testid={`admin-ebook-row-${book.id}`}>
              <img src={book.cover_url} alt="" className="w-10 h-12 object-cover border border-white/10" />
              <div>
                <p className="font-display uppercase text-sm">{book.title} {book.featured && <span className="text-primary text-[9px] tracking-widest ml-2">FEATURED</span>}</p>
                <p className="font-editorial italic text-xs text-foreground/50">{book.subtitle}</p>
              </div>
              <span className="text-sm text-foreground/70">${Number(book.price).toFixed(2)}</span>
              <span className="text-xs text-foreground/50">{book.year}</span>
              <div className="flex justify-end gap-2">
                <button onClick={() => openForm(book)} className="w-8 h-8 border border-white/20 flex items-center justify-center hover:border-primary hover:text-primary transition-colors duration-200" data-testid={`admin-edit-${book.id}`}>
                  <Pencil size={12} />
                </button>
                <button onClick={() => remove(book)} className="w-8 h-8 border border-white/20 flex items-center justify-center hover:border-primary hover:text-primary transition-colors duration-200" data-testid={`admin-delete-${book.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
          {ebooks.length === 0 && (
            <p className="px-6 py-10 text-sm text-foreground/40" data-testid="admin-empty-state">No ebooks yet. Add your first volume.</p>
          )}
        </div>
      </div>
    </main>
  );
}
